import React, { useState } from 'react'
import { Button } from 'components/Button'
import { displayWidth } from 'styles/width'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import {
    isFormSuccess,
    isFormError,
    useFormHandler,
    isSending,
} from 'hooks/useFormHandler'
import InputMask from 'react-input-mask'
import { useForm, Controller } from 'react-hook-form'
import Input, { InputProps } from '@material-ui/core/Input'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import { Modal } from 'components/Modal'
import { SendStatus } from './Form'
import { sendConversion, sendEvent } from 'tracking'
import { sendForm } from './api'
import { colors } from 'styles/colors'

const Div = styled.div`
    display: none;
    @media (min-width: ${displayWidth.tablet}) {
        display: block;
    }
`

const Unit = styled.p`
    pointer-events: none;
    color: ${colors.dark};
`
const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    @media (min-width: ${displayWidth.desktop}) {
        display: grid;
        grid-template-columns: 6fr 1fr 6fr;
    }
`
const FormWrapper = styled.div`
    position: relative;
    @media (min-width: ${displayWidth.tablet}) {
        min-width: 350px;
    }
`
const ButtonStyled = styled(Button)`
    width: 264px;
    margin: 50px auto;
    z-index: 3;
    @media (max-width: 330px) {
        width: 250px;
    }
    @media (min-width: ${displayWidth.tablet}) {
        width: 220px;
        margin: 50px 0;
    }
    @media (min-width: ${displayWidth.desktop}) {
        width: 264px;
    }
`
const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    @media (min-width: ${displayWidth.tablet}) {
        justify-content: flex-start;
    }
`

const InputBlock = styled.div`
    display: flex;
    flex-direction: column;
    @media (min-width: ${displayWidth.desktop}) {
        margin-right: 150px;
    }
    .MuiFormControl-root {
        margin: 8px 0;
    }
    .MuiSelect-root {
        max-width: calc(100vw - 88px);
        overflow: hidden;
    }
    .MuiInputBase-root {
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus {
            background-color: transparent;
            transition: background-color 5000s ease-in-out 0s;
        }
    }
    .MuiFormLabel-root {
        color: ${colors.dark};
    }
    .MuiFormLabel-root.Mui-error {
        color: ${colors.accentText} !important;
    }
    .MuiFormLabel-root.Mui-focused {
        color: ${colors.dark};
    }
    .MuiInput-underline.Mui-focused {
        :after {
            border-color: ${colors.dark};
        }
    }
`
export const ComercialForm = ({ placement }: { placement?: string }) => {
    const { t } = useTranslation()
    const formName = 'Commercial Proposall Form'
    const { errors, control, handleSubmit } = useForm({
        mode: 'onBlur',
        shouldFocusError: false,
    })

    const {
        handleSubmitStatus,
        handleFormSendStart,
        formSendStatus,
    } = useFormHandler()

    const onSubmit = (data: object) => {
        sendEvent('FormSubmitAttempt', {
            eventCategory: `Commercial`,
        })

        handleFormSendStart()
        sendForm(formName, data, placement)
            .then((success) => {
                handleSubmitStatus(success.success)

                sendConversion('FormCommercialProposal')
                sendEvent('Submit', {
                    eventCategory: `Commercial`,
                })
            })
            .catch(() => {
                handleSubmitStatus(false)
                sendEvent('FormSubmitFail', {
                    eventCategory: `Commercial`,
                })
            })
    }
    const [isOpenFormModal, setIsOpenFormModal] = useState(false)

    return (
        <FormWrapper>
            {/* Fixme: why we are not using Form component here */}
            <form onSubmit={handleSubmit(onSubmit)}>
                <InputBlock>
                    <Wrapper>
                        <FormControl
                            style={{ minWidth: 250 }}
                            error={Boolean(errors.homeType)}
                        >
                            <InputLabel>{t('homeTypeQuestion')}</InputLabel>

                            <Controller
                                as={
                                    <Select>
                                        <MenuItem value="flat">
                                            {t('homeType.flat')}
                                        </MenuItem>
                                        <MenuItem value="house">
                                            {t('homeType.house')}
                                        </MenuItem>
                                        <MenuItem value="comercialBuilding">
                                            {t('homeType.comercialBuilding')}
                                        </MenuItem>
                                    </Select>
                                }
                                error={Boolean(errors.homeType)}
                                name="homeType"
                                rules={{
                                    required: 'this is required',
                                }}
                                control={control}
                                defaultValue=""
                            />
                        </FormControl>
                        <Div />
                        <FormControl error={Boolean(errors.roomSize)}>
                            <InputLabel>{t('roomSize')}</InputLabel>
                            <Controller
                                as={Input}
                                type="number"
                                name="roomSize"
                                control={control}
                                endAdornment={
                                    <Unit>
                                        M
                                        <sup>
                                            <small>2</small>
                                        </sup>
                                    </Unit>
                                }
                                error={Boolean(errors.roomSize)}
                                defaultValue=""
                                rules={{
                                    required: 'this is required',
                                    maxLength: 10,
                                }}
                            />
                        </FormControl>
                    </Wrapper>
                    <FormControl error={Boolean(errors.availabilityDrawings)}>
                        <InputLabel>{t('availabilityDrawings')}</InputLabel>

                        <Controller
                            as={
                                <Select style={{ overflow: 'hidden' }}>
                                    <MenuItem
                                        value="noDrawing"
                                        style={{ whiteSpace: 'normal' }}
                                    >
                                        {t('noDrawing')}
                                    </MenuItem>
                                    <MenuItem
                                        value="hasMeasurement"
                                        style={{ whiteSpace: 'normal' }}
                                    >
                                        {t('hasMeasurement')}
                                    </MenuItem>
                                    <MenuItem
                                        value="hasDrawing"
                                        style={{ whiteSpace: 'normal' }}
                                    >
                                        {t('hasDrawing')}
                                    </MenuItem>
                                </Select>
                            }
                            name="availabilityDrawings"
                            rules={{
                                required: 'this is required',
                            }}
                            control={control}
                            defaultValue=""
                        />
                    </FormControl>
                    <FormControl error={Boolean(errors.location)}>
                        <InputLabel>{t('location.title')}</InputLabel>

                        <Controller
                            as={
                                <Select style={{ overflow: 'hidden' }}>
                                    <MenuItem
                                        value="kiyv"
                                        style={{ whiteSpace: 'normal' }}
                                    >
                                        {t('location.kiyv')}
                                    </MenuItem>
                                    <MenuItem
                                        value="kiyvRegion"
                                        style={{ whiteSpace: 'normal' }}
                                    >
                                        {t('location.kiyvRegion')}
                                    </MenuItem>
                                    <MenuItem
                                        value="other"
                                        style={{ whiteSpace: 'normal' }}
                                    >
                                        {t('location.other')}
                                    </MenuItem>
                                </Select>
                            }
                            name="location"
                            rules={{
                                required: 'this is required',
                            }}
                            control={control}
                            defaultValue=""
                        />
                    </FormControl>

                    <FormControl error={Boolean(errors.phone)}>
                        <InputLabel>{t('phone')}</InputLabel>

                        <Controller
                            as={
                                <InputMask
                                    maskChar={null}
                                    mask="+3\8(999) 99 999 99"
                                    type="text"
                                    inputMode="numeric"
                                    pattern="\+38+\([0-9]{3}\)\s+[0-9]{2}\s+[0-9]{3}\s+[0-9]{2}"
                                    name="phone"
                                >
                                    {(
                                        inputMaskProps: JSX.IntrinsicAttributes &
                                            InputProps
                                    ) => <Input {...inputMaskProps} />}
                                </InputMask>
                            }
                            name="phone"
                            control={control}
                            defaultValue=""
                            rules={{
                                minLength: 18,
                                required: 'this is required',
                            }}
                        />
                    </FormControl>
                    <FormControl>
                        <InputLabel error={Boolean(errors.email)}>
                            {t('email')}
                        </InputLabel>
                        <Controller
                            as={Input}
                            type="email"
                            name="email"
                            control={control}
                            error={Boolean(errors.email)}
                            rules={{
                                required: 'this is required',
                            }}
                            defaultValue=""
                        />
                    </FormControl>
                </InputBlock>
                <ButtonWrapper>
                    <ButtonStyled
                        disabled={
                            isFormSuccess(formSendStatus) ||
                            isSending(formSendStatus)
                        }
                        onClick={() => setIsOpenFormModal(true)}
                        type="submit"
                    >
                        {t('send')}
                    </ButtonStyled>
                </ButtonWrapper>
            </form>

            {isFormSuccess(formSendStatus) && (
                <Modal
                    isOpen={isOpenFormModal}
                    closeHandler={() => setIsOpenFormModal(false)}
                >
                    <SendStatus>
                        <h2>{t('isSendSuccess')}</h2>
                        <p>{t('isSendSuccessComercial')}</p>
                        <ButtonStyled
                            onClick={() => {
                                setIsOpenFormModal(false)
                            }}
                        >
                            {t('goBack')}
                        </ButtonStyled>
                    </SendStatus>
                </Modal>
            )}
            {isFormError(formSendStatus) && (
                <Modal
                    isOpen={isOpenFormModal}
                    closeHandler={() => setIsOpenFormModal(false)}
                >
                    <SendStatus>
                        <p>{t('isSendError')}</p>
                        <ButtonStyled
                            onClick={() => {
                                setIsOpenFormModal(false)
                            }}
                        >
                            {t('goBack')}
                        </ButtonStyled>
                    </SendStatus>
                </Modal>
            )}
        </FormWrapper>
    )
}
